import React, { useMemo, useState } from 'react'
import { Box, Typography, Card, useTheme, CardHeader } from '@mui/material'
import {
  Line,
  XAxis,
  YAxis,
  Legend,
  Tooltip,
  LineChart,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts'
import { CommonTable, CommonCollapse } from '@/components/common'
import { formatUtcDate } from '@/utils'

export const SingleValueHistory = ({
  data,
  title,
  icon: Icon,
  valueLabel,
  unit = '',
  yAxisLabel = '',
}) => {
  const theme = useTheme()
  const [pagination, setPagination] = useState({
    page: 0,
    perPage: 10,
    sortBy: 'date',
    sortDirection: 'desc',
  })

  const tableData = useMemo(() => {
    if (!data?.data || !Array.isArray(data.data)) return []

    return data.data.map(item => {
      const value = item.values?.[0]?.outCome
      return {
        date: formatUtcDate(item.date, 'MM/dd/yyyy'),
        value: value != null ? value : '-',
        rawDate: item.date,
        rawValue: value != null ? value : 0,
      }
    })
  }, [data])

  const chartData = useMemo(() => {
    if (!data?.data || !Array.isArray(data.data)) return []

    return data.data
      .map(item => {
        const value = item.values?.[0]?.outCome
        return {
          date: formatUtcDate(item.date, 'MM/dd/yyyy'),
          value: value != null ? value : null,
          rawDate: new Date(item.date).getTime(),
        }
      })
      .sort((a, b) => a.rawDate - b.rawDate)
  }, [data])

  const tableColumns = [
    {
      id: 'date',
      label: 'Date (PST)',
      align: 'left',
      minWidth: 120,
      sortable: true,
      sortValue: 'rawDate',
    },
    {
      id: 'value',
      label: valueLabel || title,
      align: 'center',
      minWidth: 100,
      sortable: true,
      sortValue: 'rawValue',
    },
  ]

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: 'white',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '4px',
            padding: '8px 12px',
            boxShadow: theme.shadows[3],
          }}
        >
          <Typography
            variant='body2'
            sx={{
              color: payload[0].color,
              fontWeight: 600,
            }}
          >
            {`${payload[0].name}: ${payload[0].value}${unit ? ` ${unit}` : ''}`}
          </Typography>
          {payload[0]?.payload?.date && (
            <Typography
              variant='caption'
              sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
            >
              Date: {payload[0].payload.date}
            </Typography>
          )}
        </Box>
      )
    }
    return null
  }

  if (!data || !data.data || data.data.length === 0) {
    return null
  }

  return (
    <CommonCollapse
      title={title}
      isCollapseOpen={false}
      style={{ mb: 2 }}
      id={`${title.toLowerCase().replace(/\s+/g, '-')}-collapse`}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '5fr 7fr' },
          gap: 3,
        }}
      >
        <Box>
          <CommonTable
            columns={tableColumns}
            data={tableData}
            noPagination={true}
            mode='client'
            pagination={pagination}
            onPaginationChange={setPagination}
          />
        </Box>

        <Card
          sx={{
            width: '100%',
            p: 0,
            backgroundColor: theme.palette.background.paper,
            borderRadius: '8px',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <CardHeader
            avatar={
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  backgroundColor: '#FFF1F2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {Icon && (
                  <Icon
                    sx={{
                      fontSize: '1.75rem',
                      color: theme.palette.error.main,
                    }}
                  />
                )}
              </Box>
            }
            title={
              <Typography variant='h6' fontWeight={600}>
                {title} History
              </Typography>
            }
            sx={{ my: 1 }}
          />
          <Box
            sx={{
              flex: 1,
              width: '100%',
              minHeight: '400px',
              p: 2,
              pb: 1,
            }}
          >
            <ResponsiveContainer width='100%' height={400}>
              <LineChart
                data={chartData}
                margin={{ top: 5, right: 20, left: 10, bottom: 60 }}
              >
                <CartesianGrid
                  strokeDasharray='3 3'
                  stroke={theme.palette.divider}
                />
                <XAxis
                  dataKey='date'
                  stroke={theme.palette.text.secondary}
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor='end'
                  height={80}
                />
                <YAxis
                  stroke={theme.palette.text.secondary}
                  tick={{ fontSize: 12 }}
                  label={
                    yAxisLabel
                      ? {
                          value: yAxisLabel,
                          angle: -90,
                          position: 'insideLeft',
                          style: { textAnchor: 'middle' },
                        }
                      : undefined
                  }
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ paddingTop: '10px', paddingBottom: '0px' }}
                  iconType='line'
                />
                <Line
                  type='monotone'
                  dataKey='value'
                  stroke={theme.palette.primary.main}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                  name={valueLabel || title}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Card>
      </Box>
    </CommonCollapse>
  )
}

export default SingleValueHistory
